import type { PolicySection } from '../types/policy'

export const policyMeta = {
  company: 'Fem Entity Inc.',
  title: 'Privacy Policy',
  version: 'Version 1.6',
  effectiveDate: 'Effective September 2026',
  summary:
    'How Fem Entity collects, uses, discloses, retains, and safeguards your personal information when you use our App and website.',
  pdfUrl:
    'https://241986995.fs1.hubspotusercontent-na2.net/hubfs/241986995/Privacy%20Policy%20v%201.7%20Sept%202026.pdf',
  email: 'hello@fementity.ca',
}

export const policySections: PolicySection[] = [
  {
    id: 'introduction-and-scope',
    number: 1,
    title: 'Introduction and Scope',
    blocks: [
      {
        kind: 'p',
        text: 'Fem Entity Inc. (“Fem Entity,” “we,” “us,” or “our”) is committed to protecting the privacy and confidentiality of the personal information of individuals who use the Fem Entity mobile application (“the App”) and website (collectively, the “Platform”). This Privacy Policy explains how we collect, use, disclose, retain, and safeguard your personal information when you access or use our Platform.',
      },
      {
        kind: 'p',
        text: 'This Privacy Policy applies to all users of the Fem Entity Platform in Canada, excluding the province of Quebec. The Platform is not currently available to residents of Quebec, and is designed to comply with the following privacy legislation. Where the legislation listed below applies directly to Fem Entity, we comply with it; where it applies to independent regulated practitioners using the Platform (as is the case with provincial health information legislation), we expect and require those practitioners to comply with it, as described further in Section 17.2:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'The Personal Information Protection and Electronic Documents Act (PIPEDA), S.C. 2000, c. 5, which governs private-sector commercial activities across Canada in provinces without substantially similar provincial legislation, and applies to all interprovincial and international transfers of personal information;',
          },
          {
            text: 'The Personal Information Protection Act (PIPA), SBC 2003, c. 63, which governs intra-provincial commercial activities in British Columbia; and the Personal Information Protection Act (PIPA), S.A. 2003, c. P-6.5, which governs intra-provincial commercial activities in Alberta;',
          },
          {
            text: 'Provincial health information legislation, including the Personal Health Information Protection Act, 2004 (PHIPA) in Ontario. This legislation applies where regulated health practitioners provide care through the Platform. Fem Entity is not a health information custodian under this legislation; we expect and require regulated practitioners using the Platform to independently comply with it, as described further in Section 17.2.',
          },
        ],
      },
      {
        kind: 'p',
        text: 'By downloading, accessing, or using the Fem Entity App or website, you acknowledge that you have read and understood this Privacy Policy. Where required by law, we will obtain your express or implied consent before collecting, using, or disclosing your personal information.',
      },
    ],
  },
  {
    id: 'who-we-are-and-our-commitment',
    number: 2,
    title: 'Who We Are and Our Commitment (Accountability)',
    blocks: [
      {
        kind: 'p',
        text: 'Fem Entity Inc. is a federally incorporated Canadian company based in Nova Scotia. Through the Platform, Fem Entity connects users with eligible health and wellness practitioners and provides personalized health navigation and wellness features. Practitioner eligibility and verification requirements vary by practitioner category and jurisdiction.',
      },
      {
        kind: 'p',
        text: 'In accordance with PIPEDA Principle 4.1 and applicable provincial privacy legislation, Fem Entity has designated a Privacy Officer who is accountable for the organization’s compliance with applicable privacy legislation. Our Privacy Officer is responsible for:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Overseeing all personal information handling practices;',
          },
          {
            text: 'Implementing and maintaining policies and procedures to protect personal information;',
          },
          {
            text: 'Receiving and responding to privacy complaints and inquiries;',
          },
          {
            text: 'Training staff on privacy obligations;',
          },
          {
            text: 'Ensuring contractual protections with third-party service providers; and',
          },
          {
            text: 'Communicating with the Office of the Privacy Commissioner of Canada (OPC) and the Office of the Information and Privacy Commissioner for British Columbia (BC OIPC) as required.',
          },
        ],
      },
      {
        kind: 'contact',
        title: 'Privacy Officer',
        lines: [
          {
            value: 'Mukisa Kakembo, Chief Executive Officer',
          },
          {
            label: 'Email',
            value: 'hello@fementity.ca',
            href: 'mailto:hello@fementity.ca',
          },
        ],
      },
    ],
  },
  {
    id: 'definitions',
    number: 3,
    title: 'Definitions',
    blocks: [
      {
        kind: 'lead',
        text: 'For the purposes of this Privacy Policy:',
      },
      {
        kind: 'ul',
        items: [
          {
            lead: '“Personal Information”',
            text: 'means information about an identifiable individual, as defined in section 2(1) of PIPEDA and equivalent provincial privacy legislation. This includes any information, recorded or not, that can be used alone or in combination with other information to identify an individual. It does not include the name, title, business address, or business telephone number of an employee of an organization.',
          },
          {
            lead: '“Personal Health Information”',
            text: 'means identifying information about an individual in oral or recorded form that relates to the physical or mental health of the individual, the provision of health care to the individual, a plan of service for the individual, payments or eligibility for health care or coverage for health care, the donation of body parts or bodily substances, the individual’s health number, or the identification of a health information custodian or substitute decision-maker. This definition aligns with provincial health information legislation (such as PHIPA in Ontario) where applicable.',
          },
          {
            lead: '“Sensitive Information”',
            text: 'means personal information that, by its nature, requires a higher standard of protection due to the potential for significant harm if improperly disclosed. Under PIPEDA Principle 4.3.4, medical and health records are almost always considered sensitive. In the context of our Platform, sensitive information includes all health and wellness information such as mood tracking data, health challenges, health conditions, and changes in health status.',
          },
          {
            lead: '“Consent”',
            text: 'means voluntary agreement to the collection, use, and disclosure of personal information for defined purposes. Consent may be express (active, affirmative agreement such as checking a box or signing a form) or implied (inferred from action or inaction in circumstances where the purpose would be obvious to a reasonable person).',
          },
          {
            lead: '“Platform”',
            text: 'means the Fem Entity mobile application (available on iOS and Android) and the Fem Entity website, collectively.',
          },
          {
            lead: '“Practitioner”',
            text: 'means an eligible health or wellness professional accessible through the Platform. Practitioners may include professionals regulated by a provincial regulatory body and eligible non-regulated professionals who meet Fem Entity’s applicable credential-verification requirements. A practitioner’s legal and professional obligations depend on the practitioner’s category, services, and jurisdiction.',
          },
        ],
      },
    ],
  },
  {
    id: 'what-personal-information-we-collect',
    number: 4,
    title: 'What Personal Information We Collect',
    blocks: [
      {
        kind: 'sub',
        number: '4.1',
        title: 'Personal Identifiers',
      },
      {
        kind: 'lead',
        text: 'We collect the following personal identifiers:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Full name;',
          },
          {
            text: 'Province of residence;',
          },
          {
            text: 'Email address;',
          },
          {
            text: 'Phone number; and',
          },
          {
            text: 'Payment and billing information (processed securely through Stripe; we do not store full credit card numbers on our servers).',
          },
        ],
      },
      {
        kind: 'sub',
        number: '4.2',
        title: 'Health and Wellness Information (Sensitive)',
      },
      {
        kind: 'lead',
        text: 'The following categories of information are classified as sensitive personal information under PIPEDA Principle 4.3.4 and require your express consent before collection:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Mood tracking data (emotional states, mood patterns over time);',
          },
          {
            text: 'Health challenges you identify (current health difficulties or barriers to wellness);',
          },
          {
            text: 'Changes in health status (improvements, declines, or new developments you report);',
          },
          {
            text: 'Health conditions (diagnosed or self-reported medical conditions);',
          },
          {
            text: 'Interested wellness areas, including: mental health, fitness, nutrition, career and money, mindfulness, health education, and community.',
          },
          {
            lead: 'Journal entries:',
            text: 'Free-form written reflections you create in response to journal prompts within the App. Journal entries may contain sensitive personal thoughts, feelings, and health-related information. Currently, journal entries are stored for your personal use and reflection. In the future, we may use journal entry content (with your separate express consent) to improve practitioner matching and recommend relevant wellness products or services. We will not use journal entries for any new purpose without first obtaining your express consent for that specific use.',
          },
        ],
      },
      {
        kind: 'p',
        text: 'This information is collected to personalize your experience, support practitioner matching, and deliver relevant wellness content. Providing health and wellness information is voluntary. Where Fem Entity relies on consent, we present the relevant purposes in clear language and obtain express consent before collecting or using sensitive health and wellness information. A cancellation reason that contains health-related information is treated as sensitive information and receives the same heightened protections.',
      },
      {
        kind: 'sub',
        number: '4.3',
        title: 'Service-Related Information',
      },
      {
        kind: 'lead',
        text: 'We collect the following information related to your use of our services:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Current care providers (so we can coordinate with your existing health team, where applicable);',
          },
          {
            lead: 'Insurance and benefits information:',
            text: 'If you request direct billing, we may collect information such as the name of your insurer or benefits administrator, plan or policy number, group number, member or certificate identifier, plan-holder information, your relationship to the plan holder, and other information reasonably necessary to support the direct-billing request. Providing this information is optional unless you choose to use a direct-billing service;',
          },
          {
            lead: 'Appointments and bookings, including:',
            text: 'scheduled session time ranges, appointment status, location type (virtual or in-person), cancellation reasons (if provided), and recurring appointment relationships;',
          },
          {
            text: 'Practitioner preferences (preferred practitioner type, communication style, specialty).',
          },
          {
            lead: 'Virtual appointment links:',
            text: 'When a practitioner schedules a virtual session, they provide a link to their preferred video platform (such as Zoom, Jane App, or another telehealth tool). We store the meeting link and basic session metadata (scheduled time, status) to display the link in your appointment details. Fem Entity does not host, operate, or control these video sessions — once you click the link, you leave our Platform. The video session is governed by the practitioner’s chosen platform and that platform’s own privacy policy and terms of service;',
          },
          {
            lead: 'Timezone information:',
            text: 'Your timezone and your practitioner’s timezone, used to accurately schedule and display appointment times across time zones;',
          },
          {
            lead: 'Notification delivery records:',
            text: 'We track the delivery status of communications sent to you (such as appointment reminders and wellness check-ins), including the notification channel used (e.g., push notification, email), delivery status, and scheduled and sent timestamps. This information is used to ensure reliable service communications and troubleshoot delivery issues;',
          },
          {
            lead: 'Audit logs:',
            text: 'We maintain internal audit logs that record actions taken within the Platform (such as account changes, booking modifications, and access events) for security, accountability, and troubleshooting purposes. Audit logs include an entity identifier, entity type, action performed, and timestamp; and',
          },
          {
            lead: 'Temporary slot holds:',
            text: 'When you initiate the booking process, we temporarily reserve an appointment slot for a limited time to prevent double-booking. This hold expires automatically if the booking is not completed.',
          },
        ],
      },
      {
        kind: 'sub',
        number: '4.4',
        title: 'Device and Technical Information',
      },
      {
        kind: 'lead',
        text: 'When you use the Platform, we automatically collect certain technical information, including:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Device identifiers (unique device ID, advertising identifiers);',
          },
          {
            text: 'Operating system and version (iOS or Android);',
          },
          {
            text: 'Browser information (when accessing the website);',
          },
          {
            text: 'IP address;',
          },
          {
            text: 'App usage analytics (features used, session duration, interaction patterns); and',
          },
          {
            text: 'Cookies and similar tracking technologies (on the website).',
          },
        ],
      },
      {
        kind: 'sub',
        number: '4.5',
        title: 'Mobile App Permissions',
      },
      {
        kind: 'lead',
        text: 'The Fem Entity App may request the following device permissions:',
      },
      {
        kind: 'ul',
        items: [
          {
            lead: 'Location/GPS:',
            text: 'We request access to your device’s location services to match you with practitioners by geographic proximity. Location data is used only for this purpose and is not continuously tracked in the background. You may deny or revoke this permission at any time through your device settings.',
          },
          {
            lead: 'Push Notifications:',
            text: 'We request permission to send push notifications for appointment reminders, wellness check-ins, and important service communications. You can manage notification preferences in your device settings or within the App.',
          },
          {
            lead: 'Calendar Integration:',
            text: 'We request access to your device calendar to sync appointments booked through the Platform. Calendar data is accessed solely for scheduling purposes, and we do not read or store other calendar entries.',
          },
        ],
      },
    ],
  },
  {
    id: 'how-we-collect-your-personal-information',
    number: 5,
    title: 'How We Collect Your Personal Information',
    blocks: [
      {
        kind: 'lead',
        text: 'We collect personal information through the following means:',
      },
      {
        kind: 'ul',
        items: [
          {
            lead: 'Directly from you:',
            text: 'when you create an account, complete your health profile, book appointments, communicate with practitioners, submit inquiries, or otherwise interact with the Platform;',
          },
          {
            lead: 'Automatically:',
            text: 'through the App and website via cookies, analytics tools, and device permissions you have granted;',
          },
          {
            lead: 'From practitioners:',
            text: 'information reasonably necessary to facilitate services, administer bookings, support Platform interactions, process or confirm direct-billing activity, or address safety, support, or compliance matters. Records that practitioners independently create or maintain in practitioner-controlled systems remain outside Fem Entity’s custody and control;',
          },
          {
            lead: 'From third-party service providers:',
            text: 'such as payment processors (Stripe) that confirm transaction status; and',
          },
          {
            lead: 'From public registries:',
            text: 'to verify practitioner credentials and certifications.',
          },
        ],
      },
      {
        kind: 'p',
        text: 'In all cases, personal information is collected only for identified, legitimate purposes and with appropriate consent.',
      },
    ],
  },
  {
    id: 'purposes-of-collection-use-and-disclosure',
    number: 6,
    title: 'Purposes of Collection, Use, and Disclosure',
    blocks: [
      {
        kind: 'lead',
        text: 'We collect, use, and disclose your personal information for the following purposes:',
      },
      {
        kind: 'ul',
        items: [
          {
            lead: 'Service Delivery:',
            text: 'To provide personalized health navigation, match you with appropriate practitioners, facilitate appointment booking, and deliver wellness content tailored to your needs and interests.',
          },
          {
            lead: 'Account Administration:',
            text: 'To create and manage your user account, authenticate your identity, and process payments and billing.',
          },
          {
            lead: 'Insurance and Direct Billing:',
            text: 'To make insurance and benefits information available to a practitioner you select and facilitate that practitioner’s submission of a claim or direct-billing request to an insurer, benefits administrator, or claims-processing service. Direct billing depends on the practitioner’s capabilities and the insurer’s rules, coverage, and eligibility decisions; Fem Entity does not guarantee that direct billing will be available or that a claim will be accepted or reimbursed.',
          },
          {
            lead: 'Practitioner Coordination:',
            text: 'To share relevant information with practitioners you have selected, including insurance and benefits information you provide for direct billing, so they can prepare for and deliver services and administer related billing.',
          },
          {
            lead: 'Communications:',
            text: 'To send appointment reminders, wellness check-ins, service updates, and respond to your inquiries.',
          },
          {
            lead: 'Platform Improvement:',
            text: 'To analyze usage patterns (in aggregate or de-identified form where possible) to improve the App’s functionality, user experience, and service offerings.',
          },
          {
            lead: 'Safety and Security:',
            text: 'To detect, prevent, and address fraud, unauthorized access, and technical issues.',
          },
          {
            lead: 'Legal Compliance:',
            text: 'To comply with applicable laws, regulations, court orders, or lawful requests from government authorities.',
          },
          {
            lead: 'Business Transactions:',
            text: 'In connection with a merger, acquisition, financing, or sale of assets, where personal information may be disclosed to prospective parties, subject to confidentiality agreements.',
          },
        ],
      },
      {
        kind: 'sub',
        number: '6.1',
        title: 'Automated Matching and AI-Powered Features',
      },
      {
        kind: 'p',
        text: 'Fem Entity uses algorithmic and AI-assisted tools to help match you with practitioners and to personalize the wellness content, recommendations, and check-ins you receive on the Platform. These tools draw on the personal information described in this Policy, including information in your health profile. Journal entries are not currently used for matching, recommendations, or any other purpose beyond your personal use and reflection. If we begin using journal entry content for matching or recommendations in the future, we will first obtain your separate express consent, as described in Section 4.2.',
      },
      {
        kind: 'p',
        text: 'Matching and recommendation outputs are suggestions only; they are not clinical or diagnostic determinations and do not replace the independent professional judgment of any practitioner you engage with. A human practitioner is always responsible for the care or services they provide to you, and our automated tools do not make decisions about the substance of that care.',
      },
      {
        kind: 'p',
        text: 'You may decline to provide the health information used in automated matching; if you do, you may browse and select practitioners manually rather than receiving algorithmic recommendations (see Section 7.1). If you have questions about how automated matching uses your information, you may contact our Privacy Officer at hello@fementity.ca.',
      },
      {
        kind: 'p',
        text: 'We limit the collection of personal information to what is necessary for the identified purposes, in accordance with PIPEDA Principle 4.4 and applicable provincial privacy legislation.',
      },
    ],
  },
  {
    id: 'consent',
    number: 7,
    title: 'Consent',
    blocks: [
      {
        kind: 'sub',
        number: '7.1',
        title: 'Express Consent for Sensitive Health Information',
      },
      {
        kind: 'p',
        text: 'In accordance with PIPEDA and applicable provincial privacy legislation, we obtain express, informed consent before collecting, using, or disclosing sensitive health and wellness information described in Section 4.2, unless otherwise permitted or required by law.',
      },
      {
        kind: 'lead',
        text: 'We obtain and manage consent through measures appropriate to the sensitivity of the information and the context, including:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'A clear affirmative action during onboarding or at the point of collection that explains the categories of sensitive information involved and the purposes for collecting, using, and disclosing it;',
          },
          {
            text: 'Just-in-time notices or additional consent where a materially different use or disclosure is proposed;',
          },
          {
            text: 'Plain-language information about your choices and the consequences of declining or withdrawing consent; and',
          },
          {
            text: 'Appropriate records demonstrating the consent obtained.',
          },
        ],
      },
      {
        kind: 'p',
        text: 'Providing health and wellness information is voluntary. You may use features that do not require that information, although declining may limit personalized matching, recommendations, tracking, or other features that depend on it.',
      },
      {
        kind: 'sub',
        number: '7.2',
        title: 'Implied Consent for Less Sensitive Information',
      },
      {
        kind: 'p',
        text: 'For less sensitive personal information, such as your name, email address, and basic service preferences, your consent may be implied through your voluntary provision of the information in circumstances where the purpose of collection is obvious to a reasonable person. For example, providing your email address when creating an account implies consent to use that address for account-related communications.',
      },
      {
        kind: 'sub',
        number: '7.3',
        title: 'Withdrawal of Consent',
      },
      {
        kind: 'p',
        text: 'You have the right to withdraw your consent to the collection, use, and disclosure of your personal information at any time, subject to legal or contractual restrictions, by contacting our Privacy Officer at hello@fementity.ca or through the privacy settings within the App.',
      },
      {
        kind: 'lead',
        text: 'Upon receiving your withdrawal request, we will:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Cease collecting the relevant personal information;',
          },
          {
            text: 'Cease using the information for the purposes to which consent was withdrawn;',
          },
          {
            text: 'Inform any third parties to whom the information was disclosed of the withdrawal, where feasible; and',
          },
          {
            text: 'Confirm the withdrawal in writing within a reasonable timeframe.',
          },
        ],
      },
      {
        kind: 'sub',
        number: '7.4',
        title: 'Consequences of Withdrawal',
      },
      {
        kind: 'lead',
        text: 'In accordance with PIPEDA Principle 4.3.8, we will inform you of the consequences of withdrawing consent. Depending on the scope of your withdrawal, consequences may include:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Inability to access personalized health and wellness features that depend on your health information;',
          },
          {
            text: 'Inability to match you with appropriate practitioners based on your health needs;',
          },
          {
            text: 'Loss of mood tracking history and health progress data;',
          },
          {
            text: 'Inability to receive wellness check-in notifications tailored to your health profile;',
          },
          {
            text: 'Reduction in the relevance and personalization of content and recommendations; and',
          },
          {
            text: 'In some cases, inability to continue using certain features of the Platform.',
          },
        ],
      },
      {
        kind: 'p',
        text: 'Withdrawal of consent does not affect the lawfulness of processing based on consent before its withdrawal. Basic account functionality (such as viewing general content or managing your profile) will remain available to the extent that such functions do not require the withdrawn information.',
      },
      {
        kind: 'sub',
        number: '7.5',
        title: 'Optional Marketing Communications',
      },
      {
        kind: 'p',
        text: 'Marketing consent is separate from consent needed to provide the Platform. Where consent is required, we send promotional electronic messages only after you take an affirmative, optional action to subscribe. You may withdraw marketing consent at any time using the unsubscribe mechanism in a message or by contacting us. Withdrawing marketing consent does not affect service, transactional, safety, or account communications that we may send as permitted by law.',
      },
    ],
  },
  {
    id: 'who-can-access-your-personal-information',
    number: 8,
    title: 'Who Can Access Your Personal Information',
    blocks: [
      {
        kind: 'sub',
        number: '8.1',
        title: 'Employees',
      },
      {
        kind: 'p',
        text: 'Only Fem Entity employees who have a legitimate need to access personal information to perform their duties are granted access. All employees are subject to confidentiality obligations and receive training on privacy responsibilities.',
      },
      {
        kind: 'sub',
        number: '8.2',
        title: 'Health Practitioners (Regulated and Unregulated)',
      },
      {
        kind: 'p',
        text: 'When you select, book, or engage with a practitioner through the Platform, Fem Entity may disclose relevant personal information—including health and wellness information you have consented to share and insurance or benefits information you provide for direct billing—so the practitioner can prepare for and provide services and administer related billing. The information disclosed depends on the service, your choices, and your interactions with the Platform.',
      },
      {
        kind: 'ul',
        items: [
          {
            lead: 'Regulated health practitioners:',
            text: 'Professionals governed by a provincial regulatory college (such as physicians, psychologists, registered dietitians, or registered social workers). Where a regulated practitioner provides health care through the Platform, that practitioner may be subject to provincial health information legislation (such as PHIPA in Ontario) and may have additional independent obligations regarding your personal health information.',
          },
          {
            lead: 'Eligible non-regulated professionals:',
            text: 'Professionals who are not governed by a provincial health regulatory body but who satisfy the credential, certification, insurance, and verification requirements applicable to their category. Eligible categories and requirements may change as Fem Entity updates its verification framework. Each professional must maintain the credentials required for their listing.',
          },
        ],
      },
      {
        kind: 'p',
        text: 'Practitioners must agree to confidentiality and data-protection obligations as a condition of participation. A practitioner may use the practitioner’s own electronic record, booking, telehealth, communication, billing, or practice-management systems to manage and document your care or services. If you request direct billing, the practitioner may use and disclose the insurance and benefits information reasonably necessary to submit or manage the claim with an insurer, benefits administrator, or claims-processing service. Information the practitioner independently collects, creates, receives, submits, or maintains through those systems or organizations is outside Fem Entity’s custody and control. The practitioner is independently responsible for complying with applicable privacy, security, billing, recordkeeping, professional, and consent obligations.',
      },
      {
        kind: 'sub',
        number: '8.3',
        title: 'Service Providers',
      },
      {
        kind: 'lead',
        text: 'We engage service providers to support the Platform. They may process personal information only as reasonably necessary to provide services to Fem Entity and are subject to privacy and security protections appropriate to the nature of the service and information involved. Service providers may include:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Payment processors (Stripe) for transaction processing;',
          },
          {
            text: 'Cloud hosting and data storage providers;',
          },
          {
            text: 'Communications and appointment-support providers engaged by Fem Entity;',
          },
          {
            text: 'Customer relationship management (HubSpot) for managing communications, inquiries, and client/practitioner onboarding workflows;',
          },
          {
            text: 'Analytics providers, with information limited or de-identified where reasonably appropriate;',
          },
          {
            text: 'Customer support tools; and',
          },
          {
            text: 'IT security and infrastructure providers.',
          },
        ],
      },
      {
        kind: 'p',
        text: 'Fem Entity remains accountable under applicable privacy law for personal information transferred to service providers that process it on our behalf. We use contractual and other measures appropriate to the sensitivity of the information and the nature of the service. This accountability does not extend to information that practitioners independently collect, create, receive, or maintain through practitioner-controlled systems outside Fem Entity’s custody and control.',
      },
      {
        kind: 'sub',
        number: '8.4',
        title: 'Business Transactions',
      },
      {
        kind: 'p',
        text: 'In the event of a merger, acquisition, reorganization, sale of assets, financing, or similar business transaction, personal information may be disclosed to prospective parties as part of due diligence, subject to confidentiality agreements. If such a transaction is completed, the receiving organization will be bound by the terms of this Privacy Policy until a new or amended policy is communicated to you.',
      },
      {
        kind: 'sub',
        number: '8.5',
        title: 'Legal Requirements',
      },
      {
        kind: 'lead',
        text: 'We may disclose personal information without your knowledge or consent where required or permitted by law, including:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'In response to a court order, subpoena, warrant, or other lawful demand;',
          },
          {
            text: 'To a government institution that has requested the information for purposes of enforcing a law, carrying out an investigation, or gathering intelligence;',
          },
          {
            text: 'To protect the rights, property, or safety of Fem Entity, our users, or the public; or',
          },
          {
            text: 'Where disclosure is necessary to establish, exercise, or defend legal claims.',
          },
        ],
      },
    ],
  },
  {
    id: 'cross-border-transfers',
    number: 9,
    title: 'Cross-Border Transfers',
    blocks: [
      {
        kind: 'sub',
        number: '9.1',
        title: 'Processing Outside Canada',
      },
      {
        kind: 'p',
        text: 'Fem Entity uses Supabase as a cloud database and hosting provider. Personal information processed through Supabase may be transferred to, stored in, or accessed from the United States or another jurisdiction configured for Fem Entity’s production environment. When information is processed outside Canada, it may be accessible to courts, law-enforcement, national-security, or regulatory authorities under the laws of that jurisdiction. Fem Entity has entered into a Data Processing Agreement with Supabase governing its processing and protection of personal information. A copy is available upon request, subject to any lawful confidentiality restrictions.',
      },
      {
        kind: 'sub',
        number: '9.2',
        title: 'Consequences of Processing Outside Canada',
      },
      {
        kind: 'lead',
        text: 'When personal information is processed outside Canada:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'It may be transferred to, stored in, or accessed from the United States or another jurisdiction where Fem Entity or a service provider processes information;',
          },
          {
            text: 'It may be subject to lawful access or disclosure requirements in that jurisdiction, including access by courts, law-enforcement, national-security, or regulatory authorities;',
          },
          {
            text: 'The privacy protections and remedies available in that jurisdiction may differ from those available in Canada; and',
          },
          {
            text: 'Fem Entity remains accountable for personal information transferred to service providers for processing on its behalf, as required by applicable Canadian privacy law.',
          },
        ],
      },
      {
        kind: 'sub',
        number: '9.3',
        title: 'Safeguards for Transferred Data',
      },
      {
        kind: 'lead',
        text: 'We use contractual, organizational, and technical measures appropriate to the sensitivity of the information and the risks of cross-border processing. These measures may include:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Contractual privacy and security terms with service providers, as appropriate;',
          },
          {
            text: 'Limiting transfers to information reasonably necessary for the service provider’s role;',
          },
          {
            text: 'Security measures designed to protect information during transmission and storage; and',
          },
          {
            text: 'Access restrictions and oversight proportionate to the provider, service, and information involved.',
          },
        ],
      },
      {
        kind: 'p',
        text: 'For further information about our cross-border transfer practices or to obtain the name and location of our service providers processing personal information outside Canada, please contact our Privacy Officer.',
      },
    ],
  },
  {
    id: 'retention-and-destruction',
    number: 10,
    title: 'Retention and Destruction',
    blocks: [
      {
        kind: 'sub',
        number: '10.1',
        title: 'Retention Principles',
      },
      {
        kind: 'p',
        text: 'Fem Entity retains personal information only for as long as reasonably necessary to fulfil the purposes for which it was collected, provide and secure the Platform, meet legal, tax, accounting, regulatory, and contractual obligations, resolve disputes, and establish, exercise, or defend legal claims.',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Retention periods vary according to the nature and sensitivity of the information, the purposes for which it is used, the reasonable expectations of individuals, applicable legal or professional requirements, and operational and security needs.',
          },
          {
            text: 'When an account is closed or consent is withdrawn, Fem Entity will assess what information must be deleted, de-identified, restricted, or retained. Withdrawal of consent does not require deletion where continued retention or use is permitted or required by law, or reasonably necessary for legal claims, fraud prevention, security, or another lawful purpose.',
          },
          {
            text: 'Certain financial and transaction records may be retained for periods required by tax, accounting, or other applicable law. Security, audit, backup, and system records may be retained for reasonable periods based on their purpose, integrity requirements, and technical constraints.',
          },
          {
            text: 'Practitioners may have independent professional or legal record-retention obligations for records within their custody or control. Requests concerning practitioner-controlled records should be directed to the practitioner.',
          },
        ],
      },
      {
        kind: 'sub',
        number: '10.2',
        title: 'Secure Disposal and De-identification',
      },
      {
        kind: 'p',
        text: 'When personal information is no longer reasonably required and is not subject to a legal, regulatory, professional, contractual, security, or litigation-related retention requirement, Fem Entity will securely delete, destroy, or de-identify it using measures appropriate to the nature and sensitivity of the information and the systems in which it is held.',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Residual copies may remain for a limited period in backups or system records and will be protected and addressed through the applicable backup or system lifecycle. De-identified information that can no longer reasonably be associated with an identifiable individual may be retained and used for legitimate purposes.',
          },
        ],
      },
    ],
  },
  {
    id: 'safeguards-and-security',
    number: 11,
    title: 'Safeguards and Security',
    blocks: [
      {
        kind: 'sub',
        number: '11.1',
        title: 'Risk-Based Safeguards',
      },
      {
        kind: 'p',
        text: 'Fem Entity uses reasonable administrative, technical, and physical safeguards designed to protect personal information against loss, theft, and unauthorized access, collection, use, disclosure, copying, modification, disposal, or destruction. Safeguards are selected and reviewed in light of the sensitivity, amount, format, location, and use of the information, foreseeable threats, and evolving industry practices.',
      },
      {
        kind: 'lead',
        text: 'Depending on the context, these safeguards may include:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Privacy accountability, confidentiality obligations, and policies or procedures appropriate to personnel roles;',
          },
          {
            text: 'Role-based or need-to-know access restrictions and authentication controls;',
          },
          {
            text: 'Measures designed to protect information during transmission, storage, use, backup, and disposal;',
          },
          {
            text: 'Vendor assessment, contractual protections, and oversight proportionate to the services and risks involved; and',
          },
          {
            text: 'Security maintenance, monitoring, testing, and incident-response practices appropriate to Fem Entity’s systems and risk profile.',
          },
        ],
      },
      {
        kind: 'sub',
        number: '11.2',
        title: 'Mobile Devices and External Services',
      },
      {
        kind: 'p',
        text: 'The App uses mobile-platform permissions and security features appropriate to the functions you choose to use. You control device permissions through your operating system settings. No method of electronic transmission or storage is completely secure, and Fem Entity cannot guarantee absolute security.',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Virtual appointments and other practitioner services may be delivered through systems selected and managed by the practitioner, such as video, booking, electronic record, or practice-management tools. Fem Entity does not control those practitioner-selected systems. Before using an external service, review the notice presented to you and the practitioner’s and service provider’s privacy and security practices.',
          },
        ],
      },
    ],
  },
  {
    id: 'your-privacy-rights',
    number: 12,
    title: 'Your Privacy Rights',
    blocks: [
      {
        kind: 'sub',
        number: '12.1',
        title: 'Access Rights',
      },
      {
        kind: 'lead',
        text: 'Under PIPEDA Principle 4.9 and applicable provincial privacy legislation, you have the right to request access to your personal information held by Fem Entity. Upon receiving a written request, we will:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Confirm whether we hold personal information about you;',
          },
          {
            text: 'Provide you with access to that information, subject to limited exceptions permitted by law;',
          },
          {
            text: 'Explain how the information has been used and to whom it has been disclosed; and',
          },
          {
            text: 'Provide the information in a format that is generally understandable.',
          },
        ],
      },
      {
        kind: 'sub',
        number: '12.2',
        title: 'Correction Rights',
      },
      {
        kind: 'p',
        text: 'Under PIPEDA Principle 4.9.5 and applicable provincial privacy legislation, you have the right to challenge the accuracy and completeness of your personal information and have it amended as appropriate. If we determine that the information does not need to be corrected, we will annotate your file with your requested correction and note our reasons for not making the change.',
      },
      {
        kind: 'sub',
        number: '12.3',
        title: 'Response Timing',
      },
      {
        kind: 'p',
        text: 'We respond to access and correction requests within the timelines required by the law applicable to the request. Under PIPEDA, the ordinary response period is thirty (30) calendar days, subject to a permitted extension and notice to you. Provincial legislation may prescribe different timelines or extension rules.',
      },
      {
        kind: 'sub',
        number: '12.4',
        title: 'Assistance with Requests',
      },
      {
        kind: 'p',
        text: 'We will make reasonable efforts to assist you in preparing an access or correction request. If you require assistance understanding your rights or formulating a request, please contact our Privacy Officer at hello@fementity.ca. Access will be provided at minimal or no cost, subject to any fee permitted by applicable law and advance notice to you.',
      },
    ],
  },
  {
    id: 'breach-notification',
    number: 13,
    title: 'Breach Notification',
    blocks: [
      {
        kind: 'sub',
        number: '13.1',
        title: 'Reporting to the Office of the Privacy Commissioner of Canada',
      },
      {
        kind: 'p',
        text: 'In accordance with section 10.1 of PIPEDA and the Breach of Security Safeguards Regulations (SOR/2018-64), Fem Entity will report any breach of security safeguards involving personal information under its control to the Office of the Privacy Commissioner of Canada (OPC) if the breach creates a real risk of significant harm to any individual. A report will be made as soon as feasible after the breach is discovered.',
      },
      {
        kind: 'sub',
        number: '13.2',
        title: 'Provincial Requirements',
      },
      {
        kind: 'p',
        text: 'Provincial breach-reporting and notification requirements vary. Where provincial legislation applies, Fem Entity will assess the incident and notify the applicable privacy commissioner, affected individuals, or other parties when required by that legislation. For example, Alberta PIPA requires notice to the Alberta Commissioner without unreasonable delay where a reasonable person would consider that a breach creates a real risk of significant harm. British Columbia PIPA does not currently establish an equivalent general mandatory breach-reporting regime for private-sector organizations, although notification may be appropriate as part of reasonable security and harm-mitigation measures.',
      },
      {
        kind: 'sub',
        number: '13.3',
        title: 'Notification to Affected Individuals',
      },
      {
        kind: 'p',
        text: 'Where required by PIPEDA or other applicable law, Fem Entity will notify affected individuals as soon as feasible or within the applicable statutory period. Fem Entity may also provide notice where it is a reasonable measure to mitigate potential harm. Notice will be direct unless indirect notice is permitted and appropriate in the circumstances.',
      },
      {
        kind: 'sub',
        number: '13.4',
        title: 'Content of Breach Notices',
      },
      {
        kind: 'lead',
        text: 'Breach notices to affected individuals will include:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'A description of the circumstances of the breach;',
          },
          {
            text: 'The date or period during which the breach occurred;',
          },
          {
            text: 'A description of the personal information involved in the breach;',
          },
          {
            text: 'A description of the steps Fem Entity has taken to reduce the risk of harm;',
          },
          {
            text: 'Steps the individual can take to reduce the risk of harm or mitigate potential consequences; and',
          },
          {
            text: 'Contact information for our Privacy Officer and for the OPC or BC OIPC, as applicable.',
          },
        ],
      },
      {
        kind: 'sub',
        number: '13.5',
        title: 'Determining Real Risk of Significant Harm',
      },
      {
        kind: 'lead',
        text: 'In assessing whether a breach creates a real risk of significant harm, we consider:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'The sensitivity of the personal information involved;',
          },
          {
            text: 'The probability that the information has been or will be misused; and',
          },
          {
            text: 'Any other relevant factor, including whether the information was encrypted or otherwise protected.',
          },
        ],
      },
      {
        kind: 'p',
        text: '“Significant harm” includes bodily harm, humiliation, damage to reputation or relationships, loss of employment or business opportunities, financial loss, identity theft, negative effects on credit records, and damage to or loss of property.',
      },
      {
        kind: 'sub',
        number: '13.6',
        title: 'Record-Keeping',
      },
      {
        kind: 'p',
        text: 'Fem Entity maintains a record of every breach of security safeguards involving personal information under its control, regardless of whether the breach triggers reporting or notification obligations. Records are retained for a minimum of twenty-four (24) months following the date the breach is discovered, in compliance with PIPEDA section 10.3. Records include the date of the breach, a description of the circumstances, the personal information involved, and the steps taken in response.',
      },
    ],
  },
  {
    id: 'childrens-privacy',
    number: 14,
    title: 'Children’s Privacy',
    blocks: [
      {
        kind: 'p',
        text: 'The Fem Entity Platform is intended only for individuals who are at least 18 years old. We do not knowingly permit individuals under 18 to create an account or knowingly collect personal information from them through the Platform.',
      },
      {
        kind: 'p',
        text: 'If you are under 18 years of age, please do not create an account or submit personal information through the Platform. A parent or guardian who believes that a minor has provided personal information should contact our Privacy Officer at hello@fementity.ca.',
      },
      {
        kind: 'p',
        text: 'If we learn that we collected personal information from an individual under 18 contrary to this Policy, we will take reasonable steps to restrict and delete it, subject to any applicable legal requirement to retain it.',
      },
    ],
  },
  {
    id: 'cookies-and-tracking-technologies',
    number: 15,
    title: 'Cookies and Tracking Technologies',
    blocks: [
      {
        kind: 'p',
        text: 'When you access the Fem Entity website, we may use cookies and similar tracking technologies to enhance your browsing experience and gather information about how the website is used.',
      },
      {
        kind: 'lead',
        text: 'What are cookies?',
      },
      {
        kind: 'p',
        text: 'Cookies are small text files placed on your device by a website. They store information about your browsing session and preferences.',
      },
      {
        kind: 'lead',
        text: 'Types of cookies we use:',
      },
      {
        kind: 'ul',
        items: [
          {
            lead: 'Strictly necessary cookies:',
            text: 'Required for the website to function (e.g., session management, authentication). These cannot be disabled.',
          },
          {
            lead: 'Analytics cookies:',
            text: 'Help us understand how visitors interact with our website by collecting information in aggregate form (e.g., pages visited, time on site).',
          },
          {
            lead: 'Functional cookies:',
            text: 'Remember your preferences and settings to personalize your experience.',
          },
        ],
      },
      {
        kind: 'lead',
        text: 'Managing cookies:',
      },
      {
        kind: 'p',
        text: 'You can delete or disable cookies at any time through your browser settings. Please note that disabling certain cookies may affect the functionality of the website. The Fem Entity mobile App does not use browser cookies but may use similar technologies (such as device identifiers and analytics SDKs) for equivalent purposes, as described in Section 4.4.',
      },
    ],
  },
  {
    id: 'third-party-links',
    number: 16,
    title: 'Third-Party Links',
    blocks: [
      {
        kind: 'p',
        text: 'The Platform may contain links to third-party websites, applications, or services that are not operated by Fem Entity. These links are provided for your convenience and informational purposes only.',
      },
      {
        kind: 'p',
        text: 'Fem Entity has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third-party sites or services. We encourage you to review the privacy policy of every site you visit. The inclusion of a link on our Platform does not imply endorsement or any affiliation with the linked entity.',
      },
      {
        kind: 'p',
        text: 'If you access a third-party service through the Platform (for example, a practitioner’s external booking system), any information you provide to that third party is governed by their privacy policy, not ours.',
      },
    ],
  },
  {
    id: 'province-specific-information',
    number: 17,
    title: 'Province-Specific Information',
    blocks: [
      {
        kind: 'sub',
        number: '17.1',
        title: 'British Columbia and Alberta (Provincial PIPAs)',
      },
      {
        kind: 'lead',
        text: 'If you reside in British Columbia or Alberta, your personal information may be governed by your province’s Personal Information Protection Act (BC PIPA, SBC 2003, c. 63 or Alberta PIPA, S.A. 2003, c. P-6.5) for intra-provincial activities. Under these provincial statutes:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'You may request access to and correction of your personal information, subject to the procedures, timelines, and exceptions in the applicable provincial legislation;',
          },
          {
            text: 'We will collect, use, and disclose your personal information only with your consent or as otherwise authorized under Part 3 of BC PIPA;',
          },
          {
            text: 'We will protect personal information using reasonable security arrangements and respond to privacy breaches in accordance with the law applicable to the incident;',
          },
          {
            text: 'You may file a complaint with the Office of the Information and Privacy Commissioner for British Columbia (BC OIPC) if you believe your rights under BC PIPA have been violated (see Section 19 below); and',
          },
          {
            text: 'We will retain your personal information only for as long as necessary to fulfill the purposes described in this Policy, in accordance with Part 9 of BC PIPA.',
          },
        ],
      },
      {
        kind: 'sub',
        number: '17.2',
        title: 'Health Information Legislation (Ontario and Other Provinces)',
      },
      {
        kind: 'lead',
        text: 'In certain provinces, specific health information legislation may apply where regulated health practitioners provide care through the Platform. In Ontario, for example, the Personal Health Information Protection Act, 2004 (PHIPA) governs health information custodians. In addition:',
      },
      {
        kind: 'ul',
        items: [
          {
            lead: 'PHIPA considerations:',
            text: 'Where a regulated health practitioner in Ontario provides health care through the Platform, that practitioner may be a “health information custodian” as defined in the Personal Health Information Protection Act, 2004 (PHIPA). In such cases, the practitioner is independently subject to PHIPA obligations regarding the collection, use, disclosure, retention, and security of your personal health information in connection with providing care, and Fem Entity requires practitioners on the Platform to comply with all applicable health information legislation as a condition of their participation.',
          },
          {
            text: 'Fem Entity facilitates connections and provides Platform functions. It is not intended to act as the practitioner responsible for providing health care or maintaining the practitioner’s clinical record. Whether Fem Entity has a particular role or obligation under provincial health-information legislation depends on the applicable facts, services, data flows, and law. Practitioners remain independently responsible for the records and systems within their custody or control.',
          },
          {
            text: 'If you have questions about how a regulated practitioner handles your personal health information under PHIPA, we encourage you to contact the practitioner directly or contact the Information and Privacy Commissioner of Ontario (IPC Ontario).',
          },
          {
            text: 'Questions or complaints about Fem Entity’s handling of information may be directed to Fem Entity’s Privacy Officer. Depending on the information, activity, and applicable law, a complaint may fall within the jurisdiction of the OPC or a provincial health or private-sector privacy regulator.',
          },
        ],
      },
      {
        kind: 'sub',
        number: '17.3',
        title: 'All Other Provinces and Territories (PIPEDA)',
      },
      {
        kind: 'p',
        text: 'Fem Entity Inc. is federally incorporated and based in Nova Scotia. PIPEDA generally applies to Fem Entity’s commercial handling of personal information in provinces and territories without substantially similar private-sector privacy legislation and to interprovincial and international personal-information flows, subject to the scope and exceptions of applicable law.',
      },
    ],
  },
  {
    id: 'changes-to-this-policy',
    number: 18,
    title: 'Changes to This Policy',
    blocks: [
      {
        kind: 'p',
        text: 'We may review and update this Privacy Policy from time to time to reflect changes in our practices, services, legal requirements, or for other operational reasons.',
      },
      {
        kind: 'lead',
        text: 'When we make material changes, we will:',
      },
      {
        kind: 'ul',
        items: [
          {
            text: 'Update the “Effective Date” at the top of this Policy;',
          },
          {
            text: 'Update the version number;',
          },
          {
            text: 'Notify you through a prominent notice in the App, by email, or through our website; and',
          },
          {
            text: 'Where required by law (including where changes relate to how we handle sensitive health information), obtain your renewed consent before applying the changes to previously collected information.',
          },
        ],
      },
      {
        kind: 'p',
        text: 'We encourage you to review this Policy periodically. Your continued use of the Platform after the updated Policy has been posted constitutes your acknowledgment of the changes. If you do not agree with a material change, you may close your account and cease using the Platform.',
      },
    ],
  },
  {
    id: 'complaints-and-recourse',
    number: 19,
    title: 'Complaints and Recourse',
    blocks: [
      {
        kind: 'sub',
        number: '19.1',
        title: 'Internal Complaint to Privacy Officer',
      },
      {
        kind: 'p',
        text: 'If you have concerns or complaints about how Fem Entity handles your personal information, you are encouraged to first contact our Privacy Officer directly:',
      },
      {
        kind: 'contact',
        title: 'Privacy Officer',
        lines: [
          {
            value: 'Mukisa Kakembo, CEO',
          },
          {
            value: 'Fem Entity Inc.',
          },
          {
            label: 'Email',
            value: 'hello@fementity.ca',
            href: 'mailto:hello@fementity.ca',
          },
        ],
      },
      {
        kind: 'p',
        text: 'We aim to acknowledge privacy complaints promptly and to investigate and respond within a reasonable period based on the nature and complexity of the matter. Where applicable law prescribes a timeline, we will follow that timeline and advise you if additional time is reasonably required.',
      },
      {
        kind: 'sub',
        number: '19.2',
        title:
          'Escalation to the Office of the Privacy Commissioner of Canada (OPC)',
      },
      {
        kind: 'p',
        text: 'If you are not satisfied with our response to your complaint, or if you reside in Ontario, Nova Scotia, or your complaint involves interprovincial or international data handling, you may escalate your complaint to the Office of the Privacy Commissioner of Canada:',
      },
      {
        kind: 'contact',
        title: 'Office of the Privacy Commissioner of Canada',
        lines: [
          {
            value: '30 Victoria Street, Gatineau, Quebec K1A 1H3',
          },
          {
            label: 'Toll-free',
            value: '1-800-282-1376',
            href: 'tel:18002821376',
          },
          {
            label: 'Website',
            value: 'www.priv.gc.ca',
            href: 'https://www.priv.gc.ca',
          },
        ],
      },
      {
        kind: 'p',
        text: 'The OPC has authority to investigate complaints under PIPEDA (section 11) and may make recommendations, seek mediation, or refer matters to the Federal Court of Canada.',
      },
      {
        kind: 'sub',
        number: '19.3',
        title: 'Escalation to Provincial Privacy Commissioners (BC and Alberta)',
      },
      {
        kind: 'p',
        text: 'If you reside in British Columbia or Alberta and your complaint relates to the handling of personal information subject to provincial privacy legislation, you may file a complaint with the applicable provincial privacy commissioner:',
      },
      {
        kind: 'contact',
        title:
          'Office of the Information and Privacy Commissioner for British Columbia',
        lines: [
          {
            value: 'PO Box 9038, Stn. Prov. Govt., Victoria, BC V8W 9A4',
          },
          {
            label: 'Phone',
            value: '250-387-5629',
            href: 'tel:2503875629',
          },
          {
            label: 'Toll-free',
            value: '1-800-663-7867',
            href: 'tel:18006637867',
          },
          {
            label: 'Website',
            value: 'www.oipc.bc.ca',
            href: 'https://www.oipc.bc.ca',
          },
        ],
      },
      {
        kind: 'contact',
        title: 'Office of the Information and Privacy Commissioner of Alberta',
        lines: [
          {
            label: 'Phone',
            value: '780-422-6860',
            href: 'tel:7804226860',
          },
          {
            label: 'Toll-free',
            value: '1-888-878-4044',
            href: 'tel:18888784044',
          },
          {
            label: 'Website',
            value: 'www.oipc.ab.ca',
            href: 'https://www.oipc.ab.ca',
          },
        ],
      },
      {
        kind: 'p',
        text: 'Provincial privacy commissioners have the investigative, review, order-making, and other enforcement powers provided by their applicable legislation.',
      },
    ],
  },
  {
    id: 'contact-us',
    number: 20,
    title: 'Contact Us',
    blocks: [
      {
        kind: 'p',
        text: 'If you have any questions about this Privacy Policy, wish to exercise your privacy rights, or have concerns about how Fem Entity handles your personal information, please contact:',
      },
      {
        kind: 'contact',
        title: 'Privacy Officer',
        lines: [
          {
            value: 'Mukisa Kakembo, Chief Executive Officer',
          },
          {
            value: 'Fem Entity Inc.',
          },
          {
            label: 'Email',
            value: 'hello@fementity.ca',
            href: 'mailto:hello@fementity.ca',
          },
        ],
      },
      {
        kind: 'p',
        text: 'We are committed to addressing your privacy questions and concerns in a timely and transparent manner.',
      },
    ],
  },
]
